import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Modal, Btn, Field, TextInput, Textarea, Icons } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function BrandsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", brand_id: "", name: "", base_currency: "USD", management_fee_pct: 0, notes: "" });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/brands").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/currencies").then((r) => setCurrencies(r.data));
  }, []);

  const save = async () => {
    try {
      const payload = { ...form, management_fee_pct: parseFloat(form.management_fee_pct) || 0 };
      if (editing) await api.put(`/brands/${editing.id}`, payload);
      else await api.post("/brands", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null);
      setForm({ code: "", brand_id: "", name: "", base_currency: "USD", management_fee_pct: 0, notes: "" });
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete brand?")) return;
    try { await api.delete(`/brands/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <AppShell
      title="Brands"
      subtitle="Operating brands and their base currency"
      actions={
        isAdmin && (
          <Btn data-testid="brand-add" onClick={() => { setEditing(null); setForm({ code: "", brand_id: "", name: "", base_currency: "USD", notes: "" }); setOpen(true); }}>
            <Icons.Plus size={16} className="inline mr-1" /> New brand
          </Btn>
        )
      }
    >
      <DataTable
        testid="brands-table"
        columns={[
          { key: "code", label: "Code", mono: true },
          { key: "brand_id", label: "Brand ID", mono: true, render: (r) => r.brand_id || <span className="text-[var(--wv-text-3)]">—</span> },
          { key: "name", label: "Name" },
          { key: "base_currency", label: "Currency", mono: true },
          { key: "management_fee_pct", label: "Mgmt Fee %", mono: true, render: (r) => `${Number(r.management_fee_pct || 0).toFixed(2)}%` },
          { key: "notes", label: "Notes" },
        ]}
        rows={rows}
        actions={isAdmin ? (r) => (
          <div className="flex gap-2">
            <button data-testid={`brand-edit-${r.code}`} onClick={() => { setEditing(r); setForm({ code: r.code, brand_id: r.brand_id || "", name: r.name, base_currency: r.base_currency, management_fee_pct: r.management_fee_pct || 0, notes: r.notes || "" }); setOpen(true); }} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Icons.Pencil size={16}/></button>
            <button data-testid={`brand-del-${r.code}`} onClick={() => del(r.id)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
          </div>
        ) : null}
      />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit brand" : "New brand"}
        footer={<>
          <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="brand-save" onClick={save}>Save</Btn>
        </>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code"><TextInput data-testid="brand-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WV-IN" /></Field>
          <Field label="Brand ID (from DB)"><TextInput data-testid="brand-id" value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} placeholder="1001" className="font-mono"/></Field>
          <Field label="Name"><TextInput data-testid="brand-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Wellness Vibes India" /></Field>
          <Field label="Base currency">
            <select data-testid="brand-currency" value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value })} className="w-full h-10 px-3 wv-input text-sm">
              {currencies.length === 0
                ? <option value="USD">USD (add currencies in Admin → Currencies)</option>
                : currencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </Field>
          <Field label="Management fee %"><TextInput data-testid="brand-fee" type="number" step="0.01" value={form.management_fee_pct} onChange={(e) => setForm({ ...form, management_fee_pct: e.target.value })} placeholder="5" className="font-mono"/></Field>
          <div className="col-span-2">
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></Field>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
